/**
 * =============================================================================
 * Hook: useReferralMaterials
 * Versão: 2.0.0
 *
 * Descrição:
 *   CRUD administrativo de materiais de divulgação (`affiliate_materials`).
 *   Suporta upload no bucket público `affiliate` (subpasta `materials/`).
 *   Filtra por programa e tipo. RLS no banco protege por tenant + papel.
 *
 * CHANGELOG:
 *   v2.0.0 (2026-08-17) - Vínculo N:N com programas via tabela
 *     `affiliate_material_programs`. Cada material passa a expor
 *     `program_ids` e a gravação usa a RPC `set_affiliate_material_programs`.
 *   v1.0.0 - Versão inicial (Fase 5 — Bloco 5D, item 5.10).
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useReferralMaterials');

export type AffiliateMaterialBase =
  Database['public']['Tables']['affiliate_materials']['Row'];
/** Material acrescido dos programas vinculados (N:N). */
export type AffiliateMaterial = AffiliateMaterialBase & {
  program_ids: string[];
};
export type AffiliateMaterialInsert =
  Database['public']['Tables']['affiliate_materials']['Insert'];
export type AffiliateMaterialUpdate =
  Database['public']['Tables']['affiliate_materials']['Update'];

export interface UseReferralMaterialsFilters {
  programId?: string | 'all';
  type?: string | 'all';
}

const QK = (
  tenantId: string | null | undefined,
  filters: UseReferralMaterialsFilters,
) => ['referral-materials-admin', tenantId, filters];

/**
 * Grava (substitui) os programas vinculados a um material.
 * Usa RPC atômica com validação de tenant + permissão granular.
 */
export async function saveMaterialPrograms(params: {
  materialId: string;
  programIds: string[];
}): Promise<void> {
  const { error } = await supabase.rpc('set_affiliate_material_programs', {
    p_material_id: params.materialId,
    p_program_ids: params.programIds,
  });
  if (error) {
    log.error('Erro ao salvar programas do material', error);
    throw error;
  }
}

/** Lista materiais do tenant, já com os programas vinculados. */
export function useReferralMaterials(
  filters: UseReferralMaterialsFilters = {},
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useQuery<AffiliateMaterial[]>({
    queryKey: QK(tenantId, filters),
    enabled: !!tenantId,
    queryFn: async () => {
      let query = supabase
        .from('affiliate_materials')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false })
        .limit(500);

      if (filters.type && filters.type !== 'all') {
        query = query.eq('material_type', filters.type);
      }

      const { data, error } = await query;
      if (error) {
        log.error('Erro ao listar materiais', error);
        throw error;
      }

      const rows = data ?? [];
      if (rows.length === 0) return [];

      // Busca os vínculos N:N de uma só vez (evita N+1).
      const { data: links, error: linkErr } = await supabase
        .from('affiliate_material_programs')
        .select('material_id, program_id')
        .eq('tenant_id', tenantId!)
        .in(
          'material_id',
          rows.map((r) => r.id),
        );
      if (linkErr) {
        log.error('Erro ao listar vínculos de programas', linkErr);
        throw linkErr;
      }

      const byMaterial = new Map<string, string[]>();
      (links ?? []).forEach((l) => {
        const list = byMaterial.get(l.material_id) ?? [];
        list.push(l.program_id);
        byMaterial.set(l.material_id, list);
      });

      const withPrograms: AffiliateMaterial[] = rows.map((r) => ({
        ...r,
        program_ids:
          byMaterial.get(r.id) ?? (r.program_id ? [r.program_id] : []),
      }));

      // Filtro por programa aplicado sobre os vínculos (client-side).
      if (filters.programId && filters.programId !== 'all') {
        return withPrograms.filter((m) =>
          m.program_ids.includes(filters.programId as string),
        );
      }
      return withPrograms;
    },
    staleTime: 30_000,
  });
}


/**
 * Faz upload de um arquivo no bucket `affiliate` na subpasta
 * `materials/<tenantId>/<timestamp>-<sanitizedName>`.
 * Retorna `{ storage_path, file_url, file_size_bytes, mime_type }`.
 */
export async function uploadMaterialFile(params: {
  tenantId: string;
  file: File;
}): Promise<{
  storage_path: string;
  file_url: string;
  file_size_bytes: number;
  mime_type: string;
}> {
  const { tenantId, file } = params;
  const safeName = file.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9.\-_]+/g, '-');
  const path = `materials/${tenantId}/${Date.now()}-${safeName}`;

  const { error: upErr } = await supabase.storage
    .from('affiliate')
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || undefined,
    });
  if (upErr) {
    log.error('Falha no upload do material', upErr);
    throw upErr;
  }

  const { data: pub } = supabase.storage.from('affiliate').getPublicUrl(path);
  return {
    storage_path: path,
    file_url: pub.publicUrl,
    file_size_bytes: file.size,
    mime_type: file.type || 'application/octet-stream',
  };
}

/** Mutations CRUD de materiais. */
export function useReferralMaterialMutations() {
  const qc = useQueryClient();
  const { currentTenant, user } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['referral-materials-admin'] });
    qc.invalidateQueries({ queryKey: ['affiliate-materials'] });
  };

  const createMutation = useMutation({
    mutationFn: async (
      payload: Omit<AffiliateMaterialInsert, 'tenant_id' | 'created_by'>,
    ): Promise<AffiliateMaterialBase> => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { data, error } = await supabase
        .from('affiliate_materials')
        .insert({
          ...payload,
          tenant_id: tenantId,
          created_by: user?.id ?? null,
        } as AffiliateMaterialInsert)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async (params: {
      id: string;
      patch: AffiliateMaterialUpdate;
    }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { data, error } = await supabase
        .from('affiliate_materials')
        .update(params.patch)
        .eq('id', params.id)
        .eq('tenant_id', tenantId)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  /**
   * Exclui o material e tenta remover o arquivo correspondente do storage
   * (best-effort: erro de storage não impede a exclusão do registro).
   */
  const deleteMutation = useMutation({
    mutationFn: async (material: AffiliateMaterialBase) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await supabase
        .from('affiliate_materials')
        .delete()
        .eq('id', material.id)
        .eq('tenant_id', tenantId);
      if (error) throw error;

      if (material.storage_path) {
        const { error: stErr } = await supabase.storage
          .from('affiliate')
          .remove([material.storage_path]);
        if (stErr) {
          log.warn(
            'Material excluído, mas falha ao remover arquivo do storage',
            stErr,
          );
        }
      }
    },
    onSuccess: invalidate,
  });

  return { createMutation, updateMutation, deleteMutation };
}
